import type { OutreachTab } from './contact-rows';

// The Outreach screen's tab and its two searches, carried in the URL (`/contacts?tab=inbox&q=dana`).
//
// Opening a contact is a page navigation here (`/contacts/[id]`), so the Outreach body unmounts and its
// React state goes with it: coming back used to land on whatever the default-tab rule decided, not on
// the tab the reader left — a contact opened from the Contacts tab returned them to the Inbox. Kept in
// the URL, the browser's own back restores it, while a fresh visit to `/contacts` (no params) still gets
// the default tab. iOS keeps the screen alive under the push and Android saves the state with its
// back-stack entry; this is web's version of the same.
//
// No runtime imports, so the rules are unit-testable without the app's dependency graph.

/** What the URL says, or nothing for a key it does not carry. */
export interface OutreachUrlState {
  /** Absent means "not decided yet" — the default-tab rule may still choose. */
  tab?: OutreachTab;
  /** The Contacts tab's search. */
  search: string;
  /** The Inbox tab's own search — a separate query, as on both phones. */
  inboxSearch: string;
}

type Query = Record<string, string | string[] | undefined>;

function first(value: string | string[] | undefined): string {
  return (Array.isArray(value) ? value[0] : value) ?? '';
}

/** Read the screen's state back out of a route query. An unknown `tab` is ignored, not trusted. */
export function readOutreachUrlState(query: Query): OutreachUrlState {
  const tab = first(query.tab);
  return {
    // `calls` is read back here; the screen drops it when its toggle is closed.
    tab: tab === 'contacts' || tab === 'inbox' || tab === 'calls' ? tab : undefined,
    search: first(query.q),
    inboxSearch: first(query.iq),
  };
}

/**
 * The query to write for a state, keeping every unrelated key the route already carries. Empty
 * searches are dropped rather than written as `q=`, so a clean screen keeps a clean URL.
 */
export function outreachUrlQuery(state: OutreachUrlState, current: Query): Record<string, string> {
  const next: Record<string, string> = {};
  for (const [key, value] of Object.entries(current)) {
    if (key === 'tab' || key === 'q' || key === 'iq') continue;
    const v = first(value);
    if (v) next[key] = v;
  }
  if (state.tab) next.tab = state.tab;
  if (state.search) next.q = state.search;
  if (state.inboxSearch) next.iq = state.inboxSearch;
  return next;
}

/** Whether two queries say the same thing — so the screen does not rewrite an unchanged URL. */
export function sameQuery(a: Query, b: Record<string, string>): boolean {
  const keys = new Set([...Object.keys(a).filter((key) => first(a[key])), ...Object.keys(b)]);
  for (const key of keys) {
    if (first(a[key]) !== (b[key] ?? '')) return false;
  }
  return true;
}
